import { database } from '@app/firebase';
import AppBackgroundColor from '@components/app-background-color/AppBackgroundColor';
import ProductBreadcrumbs from '@components/breadcrumbs/ProductBreadcrumbs';
import LikeButton from '@components/buttons/LikeButton';
import ProductCard from '@components/cards/ProductCard';
import HomesCarousel from '@components/carousels/HomesCarousel';
import PhotosCarousel from '@components/carousels/PhotosCarousel';
import awaitUserLogin from '@components/login/awaitUserLogin';
import UserContext from '@context/UserContext';
import Sentry from '@integrations/Sentry';
import { ContentBlock } from '@layouts/ContentBlocks';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Nl2br from '@ui/components/nl2br/Nl2br';
import useBreakpoints from '@ui/hooks/useBreakpoints';
import makeStoreUrl from '@ui/utils/makeStoreUrl';
import productMetaData from '@ui/utils/metadata/productMetaData';
import { offerPrice } from '@ui/utils/offerPrice';
import slugOrID from '@ui/utils/slugOrID';
import userFullName from '@ui/utils/userFullName';
import onSavedClickProduct from '@utils/clickHandlers/onSavedClickProduct';
import properCase from '@utils/properCase';
import { addSavedProduct, removeSavedProduct } from '@utils/savedItems';
import { productToSegmentEventData } from '@utils/segmentAnalytics';
import { makeSeoData } from 'config/next-seo.config';
import { collection, collectionGroup,doc, DocumentSnapshot, getDoc, getDocs, limit,query as firestoreQuery,QuerySnapshot,where } from 'firebase/firestore';
import { withJsonLdEscaping } from 'hoc/withJsonLdEscaping';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { NextSeo, ProductJsonLd } from 'next-seo';
import React, { useContext, useEffect } from 'react';
import { A11y, Mousewheel, Navigation, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import ErrorPage from '../../_error';
import useStyles from './products.style';

const customSort = ['new', 'used', 'refurbished', 'damaged'];

const customLookup = customSort.reduce(function (r, a, i) {
  r[a] = ('000' + i).slice(-4);
  return r;
}, {});

const sortProductOffers = (a: guesthouse.ProductOffer, b: guesthouse.ProductOffer) => {
  try {
    return (customLookup[a.type] || a.type).localeCompare(customLookup[b.type] || b.type);
  } catch (e) {
    return 0;
  }
};


export const getServerSideProps: GetServerSideProps = async ({ query }) => {
  let product: DocumentSnapshot<guesthouse.Product> | null = null;

  product = await getDoc(doc(database, `products/${query.product}`)) as DocumentSnapshot<guesthouse.Product>;

  if (!product.exists()) {
    const productDocs = await getDocs(firestoreQuery(collection(database, 'products'), where('slug', '==', query.product))) as QuerySnapshot<guesthouse.Product>;

    if (productDocs?.docs?.length) {
      product = productDocs.docs[0];
    }
  }

  if (!product?.exists()) {
    return {
      notFound: true,
    };
  }

  const productData = product.data() as guesthouse.Product;

  delete productData.pastHomes;
  delete productData.futureHomes;

  let makerProducts;

  if (productData?.owner?.docID) {
    makerProducts = await getDocs(firestoreQuery(collection(database, 'products')
      ,where('owner.docID', '==', productData.owner.docID)
      ,where('published', '==', true)
      ,where('docID', '!=', productData.docID)
      ,limit(8)));
  }

  const prices = await getDocs(firestoreQuery(collection(database, `products/${product.id}/offers`)
    ,where('stockQuantity', '>', 0)));
  const photos = await getDocs(firestoreQuery(collection(database, `products/${product.id}/photos`)
    ,where('status', '==', 'COMPLETE')));
  const productTags = await getDocs(firestoreQuery(collectionGroup(database, 'productTags')
    ,where('product.docID', '==', product.id)
    ,limit(10)));
  const dropshipOffers = await getDocs(firestoreQuery(collection(database, `products/${product.id}/offers`)
    ,where('dropship', '==', true)));

  const data = {
    key: query.product,
    id: query.product,
    product: JSON.stringify(productData || null),
    prices: JSON.stringify(prices.docs.map(p => p.data()).filter(Boolean).filter(p => !p.dropship)),
    photos: JSON.stringify(photos.docs.map(p => p.data())),
    productTags: JSON.stringify(productTags.docs.map(p => p.data())),
    makerProducts: JSON.stringify(makerProducts?.docs?.filter(p => p.id !== query.product).map(p => p.data()) || null),
    metadata: JSON.stringify(productMetaData(productData)),
    dropshipOffers: JSON.stringify(dropshipOffers.docs.map(p => p.data()))
  };

  return { props: { data } };
};

function ProductPage({ data }: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const router = useRouter();
  const { classes, theme } = useStyles();
  const { md } = useBreakpoints();

  const userContext = useContext<UserContext>(UserContext);

  const productData = JSON.parse(data.product) as guesthouse.Product;
  const offerPrices = JSON.parse(data.prices) as guesthouse.ProductOffer[];
  const photosData = JSON.parse(data.photos) as guesthouse.Photo[];
  const productTags = JSON.parse(data.productTags) as guesthouse.ProductTag[];
  const makerProducts = JSON.parse(data.makerProducts) as guesthouse.Product[];
  const metadata = JSON.parse(data.metadata) as guesthouse.MetaData;

  let currentHomes: guesthouse.ProductHome[] = [];

  if (productData?.currentHomes) {
    currentHomes = productData.currentHomes.filter(ch => ch.home.published);
  }

  useEffect(() => {
    const viewEventProduct = productToSegmentEventData(productData);

    window.analytics?.track('Product Viewed', viewEventProduct);
  }, []);

  if (data.statusCode) {
    return (<ErrorPage statusCode={data.statusCode} />);
  }

  const saved = userContext.savedProducts?.ids.indexOf(productData.docID) > -1;

  const SafeProductJsonLd = withJsonLdEscaping(ProductJsonLd);

  return (
    <>
      <AppBackgroundColor
        backgroundColor="#F7F7F7"
        headerColor="#F7F7F7"
        borderColor="#EFEFEF"
      />
      <Head>
        <title>
          {metadata.pageTitle}
        </title>

        {!productData.published && (
        <meta
          name="robots"
          content="noindex"
        />
        )}
      </Head>

      <NextSeo {...makeSeoData(metadata)} />

      <SafeProductJsonLd
        productName={productData.title}
        images={photosData.map((photo) => photo.large_url)}
        description={productData.description}
        brand={userFullName(productData.owner, true)}
        offers={offerPrices.sort(sortProductOffers).map(price => {
          const { salePrice } = offerPrice(price);

          return {
            price: salePrice.toString(),
            priceCurrency: 'USD',
            url: makeStoreUrl(router.asPath),
            availability: 'InStock',
            itemCondition: `${properCase(price.condition)} Condition`,
            seller: {
              name: userFullName(price.soldBy),
            },
          };
        })}
      />

      <ContentBlock
        margin={[4, 'auto', 6]}
      >
        <Grid container>
          <Grid
            item
            xs={12}
            md={6}
          >
            <div
              className={classes.productGallery}
              style={{ height: md ? 550 : 350 }}
            >
              {
            (Array.isArray(photosData) && photosData.length > 0)
              ? (
                <Swiper
                  modules={[Pagination, Navigation, A11y, Mousewheel]}
                  style={{
                    overflowX: 'hidden',
                    overflowY: 'visible',
                    height: md ? 550 : 350,
                    width: '100%',
                  }}
                  observer={true}
                  observeParents={true}
                  mousewheel={{
                    forceToAxis: true,
                  }}
                  freeMode={true}
                  className={classes.productGallerySlider}
                  slidesPerView={1}
                  pagination={photosData.length > 1 && {
                    clickable: true,
                    type: 'progressbar',
                  }}
                  threshold={md ? 250 : 0}
                >
                  {
                    photosData.map(photoData => {
                      const height = md ? 500 : 300;
                      const width = '100%';

                      return (
                        <SwiperSlide
                          key={photoData.docID}
                          style={{ width }}
                        >
                          <div
                            style={{
                              width,
                              height,
                              borderRadius: theme.shape.borderRadius,
                              overflow: 'hidden',
                              position: 'relative'
                            }}
                            className={classes.productGalleryImageWrapper}
                          >
                            <Image
                              key={photoData.docID}
                              fill
                              src={photoData.large_url}
                              alt=""
                              sizes="100vw"
                              style={{
                                objectFit: 'contain'
                              }}
                            />
                          </div>
                        </SwiperSlide>
                      );
                    })}
                </Swiper>
              ) : (
                <Image
                  priority
                  fill
                  src={'/images/misc/product-default.png'}
                  alt=""
                  sizes="100vw"
                  style={{
                    objectFit: 'contain'
                  }}
                />
              )
          }

            </div>
          </Grid>

          <Grid
            item
            xs={12}
            md={6}
          >
            <div className={classes.productInfo}>
              <Box
                style={{
                  marginBottom: theme.spacing(2),
                  fontSize: 12,
                  color: '#666',
                }}
              >
                <ProductBreadcrumbs product={productData} />
              </Box>

              <Box marginBottom={2}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                >
                  <Typography
                    component="h1"
                    variant="h4Alt"
                  >
                    {metadata.h1}
                  </Typography>

                  <LikeButton
                    large={true}
                    enabled={saved}
                    onClick={async () => {
                      if (userContext.user) {
                        if (saved) {
                          removeSavedProduct(userContext.user.uid, productData);
                        } else {
                          addSavedProduct(userContext.user.uid, productData);
                        }
                      } else {
                        await awaitUserLogin(userContext)
                          .then((user) => {
                            addSavedProduct(user.uid, productData);
                          });
                      }
                    }}
                  />
                </Box>
              </Box>

              <Link
                passHref
                href="/design"
              >
                <div className={classes.designConsultCTA}>
                  <Typography
                    style={{
                      fontWeight: 600,
                      fontSize: 14
                    }}
                  >
                    Get a free design consult
                  </Typography>
                </div>
              </Link>

              {productData.description && (
              <Accordion
                square
                defaultExpanded
                classes={{ root: classes.infoAccordion }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon className={classes.infoAccordionIcon} />}
                >
                  <Typography className={classes.infoQuestion}>
                    Description
                  </Typography>
                </AccordionSummary>

                <AccordionDetails>
                  <Typography className={classes.infoAnswer}>
                    <Nl2br text={productData.description} />
                  </Typography>
                </AccordionDetails>
              </Accordion>
              )}

              {productData?.dimensions_visible && (
              <Accordion
                square
                classes={{ root: classes.infoAccordion }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon className={classes.infoAccordionIcon} />}
                >
                  <Typography className={classes.infoQuestion}>
                    Dimensions
                  </Typography>
                </AccordionSummary>

                <AccordionDetails>
                  <Typography className={classes.infoAnswer}>
                    {productData.dimensions_visible}
                  </Typography>
                </AccordionDetails>
              </Accordion>
              )}


              {
            (
              !!productData.shippingPolicy ||
              !!productData.returnPolicy
            ) &&
            (
              <Accordion
                square
                classes={{ root: classes.infoAccordion }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon className={classes.infoAccordionIcon} />}
                >
                  <Typography className={classes.infoQuestion}>
                    Shipping &amp; Returns
                  </Typography>
                </AccordionSummary>

                <AccordionDetails>
                  <div>
                    {
                      !!productData.shippingPolicy &&
                      (

                        <div style={{ margin: theme.spacing(1, 0, 0) }}>
                          <Typography
                            component="span"
                            variant="h6Alt"
                            style={{ fontSize: 16, marginBottom: theme.spacing(1), display: 'block' }}
                          >
                            Shipping
                          </Typography>

                          {!!productData.shippingPolicy && (
                            <Typography className={classes.productDescriptionSmall}>
                              <Nl2br text={productData.shippingPolicy} />
                            </Typography>
                          )}

                        </div>

                      )}

                    {!!productData.returnPolicy && (
                      <div style={{ margin: theme.spacing(1, 0, 0) }}>
                        <Typography
                          component="span"
                          variant="h6Alt"
                          style={{ fontSize: 16, marginBottom: theme.spacing(1), display: 'block' }}
                        >
                          Returns
                        </Typography>

                        <Typography className={classes.productDescriptionSmall}>
                          <Nl2br text={productData.returnPolicy} />
                        </Typography>
                      </div>

                    )}
                  </div>
                </AccordionDetails>
              </Accordion>
            )
          }
            </div>
          </Grid>
        </Grid>

      </ContentBlock>

      <ContentBlock
        maxWidth={'none'}
        style={{ overflow: 'hidden' }}
      >
        <ContentBlock
          margin={[0, 'auto', 0]}
          marginMd={[6, 'auto', 12]}
        >

          {productTags.length > 0 ? (
            <PhotosCarousel
              photos={productTags.map((productTag) => productTag.photo)}
            />
          ) : null}

          {!!(makerProducts?.length > 0) && (
          <>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: 'row',
                marginBottom: theme.spacing(3),
                marginTop: theme.spacing(10)
              }}
            >
              <Typography
                gutterBottom
                variant="h5Alt"
                component="h2"
              >
                More products from
                {' '}
                {userFullName(productData.owner, true)}
              </Typography>
              {md ?
                <Link
                  passHref
                  legacyBehavior
                  href={`/shop/makers/${slugOrID(productData.owner)}`}
                >
                  <Typography
                    fontFamily={theme.gh_vars.circular}
                    component="a"
                    display="block"
                    style={{ fontSize: 14, lineHeight: 1.75, marginTop: theme.spacing(1), cursor: 'pointer', color: theme.palette.common.black }}
                  >
                    View All
                  </Typography>
                </Link>
                : null
            }
            </div>

            <Grid
              container
              spacing={3}
            >
              {makerProducts.length > 0 && makerProducts.map((product) => {
                const saved = userContext.savedProducts?.ids.indexOf(product.docID) > -1;

                return (
                  <Grid
                    key={product.docID}
                    item
                    xs={6}
                    md={3}
                  >
                    <ProductCard
                      product={product}
                      saved={saved}
                      onClick={() => {
                        try {
                          window.analytics?.track('Product Clicked', productToSegmentEventData(product));
                        } catch (e) {
                          Sentry.captureException(e);
                        }
                      }}
                      onSavedClick={async (e) => onSavedClickProduct(userContext, saved, product as guesthouse.Product, e)}
                    />
                  </Grid>
                );
              })}
            </Grid>
            {!md ?
              <Link
                passHref
                legacyBehavior
                href={`/shop/makers/${slugOrID(productData.owner)}`}
              >
                <Typography
                  fontFamily={theme.gh_vars.circular}
                  component="a"
                  display="block"
                  style={{ fontSize: 14, lineHeight: 1.75, marginTop: theme.spacing(3), cursor: 'pointer', color: theme.palette.common.black }}
                >
                  View All
                </Typography>
              </Link>
              : null
          }
          </>
          )}

          {!!currentHomes?.length && (
          <Typography
            gutterBottom
            variant="h5Alt"
            component="h2"
            style={{ marginTop: theme.spacing(6), marginBottom: theme.spacing(3) }}
          >
            See it in person
          </Typography>
          )}

          {!!currentHomes?.length && (
          <HomesCarousel
            seeAll
            homes={currentHomes.map(ch => ch.home)}
          />
          )}
        </ContentBlock>
      </ContentBlock>
    </>
  );
}

export default ProductPage;
